import { z } from 'zod';
import { Email, IsoDateTime, NonEmptyName, Pence, Uuid } from '../primitives.js';

/**
 * Receipts + VAT contract (Phase 2). Entity/response schemas carry `.meta({ id })`; query/param
 * schemas must NOT. Money is integer pence; unit prices on inputs are VAT-INCLUSIVE (UK convention).
 */

// ── Enums ────────────────────────────────────────────────────────────────────
export const ReceiptLineKind = z.enum([
  'service',
  'product',
  'deposit_redeemed',
  'discount',
  'tip',
  // WS9 — a voucher/gift-card SALE line. An MPV sale rides the vatExempt → 0 bps path (no VAT and £0
  // recognised revenue at sale — the cash leg posts to the gift-card liability ledger, not revenue;
  // ADR-0008 §2). An SPV sale is taxable at issue. The till nets it; it is not itself a taxable supply
  // for an MPV. Redemption is a TENDER (a payment method), never a line of this kind.
  'voucher_sale',
]);
export type ReceiptLineKind = z.infer<typeof ReceiptLineKind>;

export const ReceiptStatus = z.enum(['issued', 'voided']);
export type ReceiptStatus = z.infer<typeof ReceiptStatus>;

// ── Line item + VAT building blocks ────────────────────────────────────────────
export const ReceiptLineItem = z
  .object({
    id: Uuid,
    lineNo: z.int().min(1),
    kind: ReceiptLineKind,
    serviceId: Uuid.nullable(),
    productId: Uuid.nullable(),
    description: z.string(),
    qty: z.int().min(1),
    unitPricePence: Pence,
    lineGrossPence: Pence,
    lineExVatPence: Pence,
    lineVatPence: Pence,
    vatRateBps: z.int().min(0).max(10000),
    performedByStaffId: Uuid.nullable(),
    performedByName: z.string().nullable(),
  })
  .meta({ id: 'ReceiptLineItem' });
export type ReceiptLineItem = z.infer<typeof ReceiptLineItem>;

export const VatBreakdownBand = z
  .object({
    rateBps: z.int().min(0).max(10000),
    exVatPence: Pence,
    vatPence: Pence,
    grossPence: Pence,
  })
  .meta({ id: 'VatBreakdownBand' });
export type VatBreakdownBand = z.infer<typeof VatBreakdownBand>;

export const ReceiptPerformedByLine = z
  .object({
    staffId: Uuid.nullable(),
    name: z.string(),
    grossPence: Pence,
  })
  .meta({ id: 'ReceiptPerformedByLine' });
export type ReceiptPerformedByLine = z.infer<typeof ReceiptPerformedByLine>;

// ── Receipt (entity) ───────────────────────────────────────────────────────────
export const Receipt = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    paymentId: Uuid,
    appointmentId: Uuid.nullable(),
    clientId: Uuid.nullable(),
    templateId: Uuid.nullable(),
    receiptNumber: z.int().min(1),
    receiptRef: z.string(),
    status: ReceiptStatus,
    vatRegistered: z.boolean(),
    vatNumber: z.string().nullable(),
    vatRateBps: z.int().min(0).max(10000),
    currency: z.literal('GBP'),
    subtotalExVatPence: Pence,
    vatTotalPence: Pence,
    grossTotalPence: Pence,
    vatBreakdown: z.array(VatBreakdownBand),
    performedBy: z.array(ReceiptPerformedByLine),
    lineItems: z.array(ReceiptLineItem),
    headerText: z.string().nullable(),
    footerText: z.string().nullable(),
    nextAppointmentAt: IsoDateTime.nullable(),
    clientName: z.string(),
    clientEmail: Email.nullable(),
    publicUrl: z.string(),
    issuedByStaffId: Uuid.nullable(),
    issuedAt: IsoDateTime,
    emailedAt: IsoDateTime.nullable(),
    voidedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Receipt' });
export type Receipt = z.infer<typeof Receipt>;

// ── Requests ─────────────────────────────────────────────────────────────────
export const IssueReceiptLine = z
  .object({
    kind: ReceiptLineKind,
    serviceId: Uuid.nullish(),
    productId: Uuid.nullish(),
    description: z.string().min(1).max(200),
    qty: z.int().min(1).default(1),
    unitPricePence: Pence,
    vatExempt: z.boolean().default(false),
    performedByStaffId: Uuid.nullish(),
  })
  .meta({ id: 'IssueReceiptLine' });
export type IssueReceiptLine = z.infer<typeof IssueReceiptLine>;

export const IssueReceipt = z
  .object({
    paymentId: Uuid,
    templateId: Uuid.optional(),
    clientId: Uuid.nullish(),
    appointmentId: Uuid.nullish(),
    nextAppointmentAt: IsoDateTime.nullish(),
    lineItems: z.array(IssueReceiptLine).optional(),
    /**
     * WS14 — tips captured at the till. Each entry is a gratuity attributed to a staff member; the
     * server materialises one VAT-0 `tip` receipt line per entry (100% the staff member's, never
     * commissionable). The till charges the FULL amount (service + tips) on the payment intent, then
     * passes `tips` here. Reconciliation guard: Σtips must be ≤ payment.amountPence and the service
     * portion (explicit lineItems OR derived) must equal payment.amountPence − Σtips, so the receipt
     * grand total always equals the payment amount (shown==charged).
     */
    tips: z.array(z.object({ staffId: Uuid, amountPence: Pence.min(1) })).optional(),
    sendEmail: z.boolean().default(false),
    idempotencyKey: z.string().min(8).max(200).optional(),
  })
  .meta({ id: 'IssueReceipt' });
export type IssueReceipt = z.infer<typeof IssueReceipt>;

// ── Templates ──────────────────────────────────────────────────────────────────
export const ReceiptTemplate = z
  .object({
    id: Uuid,
    salonId: Uuid,
    name: NonEmptyName,
    isDefault: z.boolean(),
    logoUrl: z.string().nullable(),
    headerText: z.string().nullable(),
    footerText: z.string().nullable(),
    showVatBreakdown: z.boolean(),
    showPerformedBy: z.boolean(),
    showNextAppointment: z.boolean(),
    thermalWidthChars: z.union([z.literal(32), z.literal(42), z.literal(48)]),
    active: z.boolean(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'ReceiptTemplate' });
export type ReceiptTemplate = z.infer<typeof ReceiptTemplate>;

export const CreateReceiptTemplate = z
  .object({
    name: NonEmptyName,
    isDefault: z.boolean().default(false),
    logoUrl: z.string().url().max(500).nullish(),
    headerText: z.string().max(500).nullish(),
    footerText: z.string().max(1000).nullish(),
    showVatBreakdown: z.boolean().default(true),
    showPerformedBy: z.boolean().default(true),
    showNextAppointment: z.boolean().default(true),
    thermalWidthChars: z.union([z.literal(32), z.literal(42), z.literal(48)]).default(42),
  })
  .meta({ id: 'CreateReceiptTemplate' });
export type CreateReceiptTemplate = z.infer<typeof CreateReceiptTemplate>;

export const UpdateReceiptTemplate = CreateReceiptTemplate.partial()
  .extend({ active: z.boolean().optional() })
  .meta({ id: 'UpdateReceiptTemplate' });
export type UpdateReceiptTemplate = z.infer<typeof UpdateReceiptTemplate>;

// ── Public + thermal views ──────────────────────────────────────────────────────
export const PublicReceiptLine = z
  .object({
    description: z.string(),
    qty: z.int().min(1),
    unitPricePence: Pence,
    lineGrossPence: Pence,
    performedByName: z.string().nullable(),
  })
  .meta({ id: 'PublicReceiptLine' });
export type PublicReceiptLine = z.infer<typeof PublicReceiptLine>;

export const PublicReceipt = z
  .object({
    receiptRef: z.string(),
    salonName: z.string(),
    logoUrl: z.string().nullable(),
    vatRegistered: z.boolean(),
    vatNumber: z.string().nullable(),
    issuedAt: IsoDateTime,
    lineItems: z.array(PublicReceiptLine),
    vatBreakdown: z.array(VatBreakdownBand),
    performedBy: z.array(ReceiptPerformedByLine),
    subtotalExVatPence: Pence,
    vatTotalPence: Pence,
    grossTotalPence: Pence,
    headerText: z.string().nullable(),
    footerText: z.string().nullable(),
    nextAppointmentAt: IsoDateTime.nullable(),
  })
  .meta({ id: 'PublicReceipt' });
export type PublicReceipt = z.infer<typeof PublicReceipt>;

export const ThermalReceiptResponse = z
  .object({
    widthChars: z.int(),
    lines: z.array(z.string()),
    escposBase64: z.string(),
  })
  .meta({ id: 'ThermalReceiptResponse' });
export type ThermalReceiptResponse = z.infer<typeof ThermalReceiptResponse>;
